/**
 * 表单验证模式配置文件
 * 集中管理所有表单验证的正则表达式和错误消息
 *
 * @author esvisa-web team
 * @version 1.0.0
 */

/**
 * 表单验证正则表达式模式
 * 定义各种输入字段的格式验证规则
 */
export const validationPatterns = {
    // ==================== 基础文本格式验证 ====================

    /**
     * 英文字母格式
     * 要求：首字符必须是字母，后续可以是字母和空格
     */
    english: /^[A-Za-z][A-Za-z ]*$/,

    /**
     * 数字英文混合格式
     * 要求：首字符不能是空格，后续可以是数字和英文字母
     */
    numberOrEnglish: /^[A-Za-z0-9][A-Za-z0-9 ]*$/,

    /**
     * 中文英文混合格式
     * 要求：首字符必须是中文或英文字母，后续可以是中文、英文字母、连字符和空格
     */
    chinaOrEnglish: /^[A-Za-z\u4E00-\u9FA5][A-Za-z\u4E00-\u9FA5\- ]*$/,

    /**
     * 数字中文英文混合格式（包含特殊符号）
     * 允许：数字、中文、英文和特殊符号
     */
    numberOrChinaOrEnglish: /^[A-Za-z0-9\u4E00-\u9FA5#$*%&(;)!@^?><.', -]*$/,

    /**
     * 数字中文英文混合格式
     * 要求：首字符不能是空格，后续可以是数字和英文字母中文
     */
    numberOrChinaOrEnglish1: /^[A-Za-z0-9\u4E00-\u9FA5][A-Za-z0-9\u4E00-\u9FA5 ]*$/,

    // ==================== 组织名称和地址格式验证 ====================

    /**
     * 组织名称格式
     * 允许：单引号、空格、字母、数字、中文、连字符
     */
    orgName: /^[' A-Za-z0-9\u4E00-\u9FA5\\-]*$/,

    /**
     * 地址格式（支持中文）
     * 允许：常用符号、字母、数字、中文
     */
    address: /^[,.&'*^$()#%&;!@?>< A-Za-z0-9\u4E00-\u9FA5\\-]*$/,

    /**
     * 英文地址格式
     * 允许：常用符号、字母、数字
     */
    englishAddress: /^[,.&'*^$()#%&;!@?>< A-Za-z0-9\\-]*$/,


    // ==================== 证件号码格式验证 ====================

    /**
     * 身份证号码格式
     * 支持：15位、18位或17位+X
     */
    nationalIdentificationNumber: /^(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)|(^([A-Z]|[0-9]){5,17}$)$/,

    /**
     * 护照号码格式
     * 要求：5-17位字母和数字
     */
    passportNumber: /^[A-Za-z0-9]{5,17}$/,

    /**
     * 签证号码格式
     * 要求：8位字母和数字
     */
    visaNumber: /^[A-Za-z0-9]{8}$/,

    /**
     * SEVIS ID格式
     * 要求：字母、数字、逗号和空格
     */
    sevisId: /^[A-Za-z0-9][A-Za-z0-9, ]*$/,

    // ==================== 邮政编码格式验证 ====================

    /**
     * 中国邮政编码格式
     * 要求：5-6位字母和数字
     */
    zipCode: /^[A-Za-z0-9]{5,6}$/,

    /**
     * 通用邮政编码格式
     * 要求：3-10位邮政编码
     */
    globalZipCode: /^(?! )[A-Za-z0-9 -]{3,10}(?<! )$/,
    /**
     * 美国邮政编码格式
     * 要求：请输入5位邮编或5位+4位（如 12345 或 12345-6789）
     */
    usaZipCode: /^\d{5}(-\d{4})?$/,

    // ==================== 电话号码格式验证 ====================

    /**
     * 手机号码格式
     * 要求：10-13位数字
     */
    cellphone: /^\d{10,13}$/,

    /**
     * 电话号码格式
     * 要求：10-13位数字
     */
    phone: /^\d{10,13}$/,

    /**
     * 美国手机号码格式
     * 要求：标准美国手机号码格式验证
     */
    usaCellphone: /^((\+?1)?[2-9]\d{2}[2-9](?!11)\d{6})$/,

    // ==================== 其他证件格式验证 ====================

    /**
     * 社会安全号码格式
     * 要求：123-45-6789格式
     */
    socialSecurityNumber: /^\d{3}-\d{2}-\d{4}$/,

    /**
     * 税号格式
     * 要求：9位数字
     */
    taxId: /^\d{9}$/,

    /**
     * 驾照号码格式
     * 要求：6-14位大写字母和数字
     */
    driverLicenseNumber: /^[A-Z0-9]{6,14}$/,

    /**
     * 航班号格式
     * 要求：字母、数字和空格
     */
    flightNumber: /^[A-Za-z0-9 ]+$/,
};

/**
 * 表单验证错误消息配置
 * 用于显示各种输入字段的格式要求和错误提示
 */
export const validationMessages = {
    // ==================== 基础文本格式 ====================
    english: '请输入英文字母和空格',
    numberOrEnglish: '请输入数字和英文字母',
    chinaOrEnglish: '请输入中文、英文字母和空格',
    numberOrChinaOrEnglish: '请输入数字、中文、英文字母和半角特殊符号',
    numberOrChinaOrEnglish1: '请输入数字、中文、英文字母',

    // ==================== 组织名称和地址格式 ====================
    orgName: '请输入数字、中文、英文字母和空格',
    address: '请输入数字、中文、英文字母和常用符号',
    englishAddress: '请输入数字、英文字母和常用符号',


    // ==================== 证件号码格式 ====================
    nationalIdentificationNumber: '请输入有效的身份证号码',
    passportNumber: '请输入5-17位数字或大写字母',
    visaNumber: '请输入8位字母和数字组合',
    sevisId: '请输入英文字母、数字和逗号',

    // ==================== 邮政编码格式 ====================
    zipCode: '请输入5-6位数字',
    usaZipCode: '请输入5位邮编或5位+4位（如 12345 或 12345-6789）',
    globalZipCode: '请输入3-10位邮政编码（支持字母、数字、空格和-）',

    // ==================== 电话号码格式 ====================
    cellphone: '请输入10-13位数字',
    phone: '请输入10-13位数字',
    usaCellphone: '请输入有效的美国手机号码',

    // ==================== 其他证件格式 ====================
    socialSecurityNumber: '请输入格式：123-45-6789',
    taxId: '请输入9位数字',
    driverLicenseNumber: '请输入6-14位大写字母和数字',
    flightNumber: '请输入航班号（字母和数字）',
};


/**
 * 验证输入值是否符合指定模式
 * @param {string} value - 要验证的值
 * @param {string} patternName - 验证模式名称
 * @returns {boolean} 验证结果
 */
export function validateValue(value, patternName) {
    const pattern = validationPatterns[patternName];
    if (!pattern) {
        console.warn(`未找到验证模式: ${patternName}`);
        return true;
    }
    return pattern.test(value);
}

/**
 * 获取验证错误消息
 * @param {string} patternName - 验证模式名称
 * @returns {string} 错误消息
 */
export function getValidationMessage(patternName) {
    return validationMessages[patternName] || '输入格式不正确';
}


export const validationRule = {};

for (const key in validationPatterns) {
    validationRule[key] = {
        pattern: validationPatterns[key],
        message: validationMessages[key]
    };
}
